import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import logibudLogo from "../assets/logo/logibud_primary_darkblue.svg";
import shopifyLogo from "../assets/integrations/shopify-logo.svg";
import { useUserSession } from "../context/UserContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { useUserWorkspaces } from "../hooks";
import { WorkspaceRole } from "../types";
import type { ILocation } from "../types";
import { listLocations } from "../data/api/locations";
import { linkShopifyInstallation } from "../api/shopifyIntegrations";

const ShopifyWorkspaceLinkPage = () => {
  const [searchParams] = useSearchParams();
  const state = searchParams.get("state") || "";
  const { userSession, authReady } = useUserSession();
  const { activeWorkspace, setActiveWorkspace } = useWorkspace();
  const workspacesQuery = useUserWorkspaces(userSession?.user.email || "");
  const [isLinking, setIsLinking] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string>();
  const [locations, setLocations] = useState<ILocation[]>([]);
  const [pickupLocationId, setPickupLocationId] = useState("");
  const [locationsLoading, setLocationsLoading] = useState(false);
  const continuePath = `/shopify/connect?state=${encodeURIComponent(state)}`;
  const workspaces = useMemo(
    () =>
      (workspacesQuery.data || []).filter((workspace) =>
        [WorkspaceRole.Owner, WorkspaceRole.Administrator].includes(
          workspace.role,
        ),
      ),
    [workspacesQuery.data],
  );
  const selected =
    workspaces.find(
      (workspace) => workspace.workspaceId === activeWorkspace?.workspaceId,
    ) || workspaces[0];

  const loadLocations = useCallback(async () => {
    if (!userSession || !selected?.workspaceId) {
      setLocations([]);
      setPickupLocationId("");
      return;
    }
    setLocationsLoading(true);
    try {
      const values = await listLocations(
        userSession.token.accessToken,
        selected.workspaceId,
      );
      setLocations(values);
      setPickupLocationId((current) =>
        values.some((location) => location.id === current)
          ? current
          : values[0]?.id || "",
      );
      setError(undefined);
    } catch (requestError: any) {
      setError(requestError?.message || "Could not load pickup locations");
    } finally {
      setLocationsLoading(false);
    }
  }, [selected?.workspaceId, userSession]);

  useEffect(() => {
    void loadLocations();
  }, [loadLocations]);

  useEffect(() => {
    const handleFocus = () => void loadLocations();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [loadLocations]);

  const link = async () => {
    if (!userSession || !selected || !state) return;
    setIsLinking(true);
    setError(undefined);
    try {
      await linkShopifyInstallation(
        userSession.token.accessToken,
        selected.workspaceId,
        state,
        pickupLocationId,
      );
      setIsComplete(true);
      window.opener?.postMessage(
        { type: "logibud:shopify-linked" },
        window.location.origin,
      );
      window.setTimeout(() => window.close(), 900);
    } catch (requestError: any) {
      setError(requestError?.message || "Could not connect this workspace");
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <main className="min-h-screen bg-gray-50 px-4 py-10 text-gray-900">
      <section className="mx-auto max-w-lg rounded-lg border border-gray-200 bg-white p-7 shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 pb-5">
          <img alt="Logibud" className="h-8" src={logibudLogo} />
          <img alt="Shopify" className="h-9" src={shopifyLogo} />
        </div>
        <h1 className="mt-7 text-2xl font-semibold">
          Choose Logibud workspace
        </h1>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          Shopify orders will be sent to the selected workspace.
        </p>

        {!authReady ? (
          <p className="mt-6 rounded-md bg-gray-100 p-4 text-sm">
            Checking login…
          </p>
        ) : !userSession ? (
          <div className="mt-6 rounded-md border border-yellow-200 bg-yellow-50 p-4 text-sm">
            Sign in before connecting Shopify.
            <Link
              className="mt-3 block font-medium text-lgb-primary hover:underline"
              to={`/login?continue=${encodeURIComponent(continuePath)}`}
            >
              Sign in to Logibud
            </Link>
          </div>
        ) : isComplete ? (
          <div className="mt-6 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-900">
            Connected. You can return to Shopify.
          </div>
        ) : (
          <div className="mt-6 space-y-5">
            {workspacesQuery.isLoading ? (
              <p className="text-sm text-gray-600">Loading workspaces…</p>
            ) : workspaces.length ? (
              <label className="block text-sm font-medium">
                Workspace
                <select
                  className="mt-2 block w-full rounded-md border-gray-300 bg-white focus:border-lgb-primary focus:ring-lgb-primary"
                  value={selected?.workspaceId || ""}
                  onChange={(event) => {
                    const workspace = workspaces.find(
                      (item) => item.workspaceId === event.target.value,
                    );
                    if (workspace) setActiveWorkspace(workspace);
                  }}
                >
                  {workspaces.map((workspace) => (
                    <option
                      key={workspace.workspaceId}
                      value={workspace.workspaceId}
                    >
                      {workspace.workspaceName || workspace.workspaceId}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <p className="rounded-md border border-yellow-200 bg-yellow-50 p-4 text-sm">
                You need Owner or Administrator access to connect Shopify.
              </p>
            )}
            {selected && (
              <div>
                <label className="block text-sm font-medium">
                  Pickup location
                  <select
                    className="mt-2 block w-full rounded-md border-gray-300 bg-white focus:border-lgb-primary focus:ring-lgb-primary"
                    disabled={locationsLoading || !locations.length}
                    value={pickupLocationId}
                    onChange={(event) =>
                      setPickupLocationId(event.target.value)
                    }
                  >
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.displayName ||
                          location.addressLine ||
                          location.id}
                      </option>
                    ))}
                  </select>
                </label>
                {!locationsLoading && !locations.length && (
                  <div className="mt-3 rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm font-normal text-yellow-900">
                    A pickup location is required to create transport drafts.
                    <div className="mt-3 flex items-center gap-4">
                      <a
                        className="font-medium text-lgb-primary hover:underline"
                        href="/resources/locations"
                        rel="noreferrer"
                        target="_blank"
                      >
                        Create pickup location
                      </a>
                      <button
                        className="font-medium text-lgb-primary hover:underline"
                        onClick={() => void loadLocations()}
                        type="button"
                      >
                        Refresh locations
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {error && <p className="text-sm text-red-700">{error}</p>}
            <button
              className="w-full rounded-md bg-lgb-primary px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
              disabled={!selected || !pickupLocationId || !state || isLinking}
              onClick={() => void link()}
            >
              {isLinking ? "Connecting…" : "Connect workspace"}
            </button>
          </div>
        )}
      </section>
    </main>
  );
};

export default ShopifyWorkspaceLinkPage;
